import { isMemberAnAdmin, isSystemAccount } from "@/core/auth";
import type { Member } from "@/types/Member";

/**
 * Client mirror of the R24a role-aware pull-forward minimum notice. The server
 * (ember/src/utils/bookingScheduleRules.ts) is the authority; this only drives the UI greying of
 * "Start Slot Here" rows that are too soon for the current user's role.
 *
 * - Practice manager: must leave 7 days' notice so finance can set billing up.
 * - Admin / superadmin / system: effectively no limit — can pull a slot to tomorrow.
 */
export const SLOT_PULL_FORWARD_MIN_NOTICE_DAYS_ADMIN = 1;
export const SLOT_PULL_FORWARD_MIN_NOTICE_DAYS_PM = 7;

/** Admin-level roles (admin/superadmin/system) get the 1-day window; everyone else the 7-day. */
export function slotPullForwardMinNoticeDays(member: Member | null | undefined): number {
  const isAdminLevel = isMemberAnAdmin(member ?? null) || isSystemAccount(member ?? null);
  return isAdminLevel ? SLOT_PULL_FORWARD_MIN_NOTICE_DAYS_ADMIN : SLOT_PULL_FORWARD_MIN_NOTICE_DAYS_PM;
}

/**
 * The earliest date (UTC start-of-day) the given user may pull a slot start forward to:
 * `today + minNoticeDays`. Occurrences before this are too soon and must be disabled.
 */
export function earliestSlotPullForwardDate(member: Member | null | undefined, today: Date = new Date()): Date {
  const earliest = new Date(today);
  earliest.setUTCHours(0, 0, 0, 0);
  earliest.setUTCDate(earliest.getUTCDate() + slotPullForwardMinNoticeDays(member));
  return earliest;
}

/**
 * Whether the given user's pull-forward choices are restricted to first-week-of-month dates. A
 * practice manager may only start a slot in the first week of a month; admin/superadmin/system are
 * unrestricted. Mirrors the admin-level split of `slotPullForwardMinNoticeDays`. Server is the
 * authority (`bookingScheduleRules.slotPullForwardFirstWeekOfMonthOnly`); this drives the UI hiding.
 */
export function slotPullForwardFirstWeekOfMonthOnly(member: Member | null | undefined): boolean {
  const isAdminLevel = isMemberAnAdmin(member ?? null) || isSystemAccount(member ?? null);
  return !isAdminLevel;
}

/**
 * Whether `date` falls in the first week of its month — i.e. day-of-month 1–7. For a weekly slot
 * every candidate is on the slot's weekday, so this is "the first occurrence of that weekday in the
 * month". Uses the local calendar day, matching how occurrence rows render and serialise their ISO
 * date (which the server then re-parses as the same UTC calendar day).
 */
export function isFirstWeekOfMonth(date: Date): boolean {
  return date.getDate() <= 7;
}
